import React from 'react';
import { Box, Chip, IconButton, Paper, Tooltip } from '@mui/material';
import { Close as CloseIcon, Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const imageKey = (image: any): string => (image.id ? `id-${image.id}` : image.url);

interface ItemProps {
  image: any;
  index: number;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onMakeCover: () => void;
}

const SortableImage: React.FC<ItemProps> = ({ image, index, selected, disabled, onSelect, onRemove, onMakeCover }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: imageKey(image),
    disabled,
  });
  const isCover = index === 0;

  return (
    <Paper
      ref={setNodeRef}
      elevation={isDragging ? 8 : selected ? 4 : 1}
      onClick={onSelect}
      {...attributes}
      {...listeners}
      aria-label={`Foto ${index + 1}${isCover ? ', capa' : ''}. Arraste para mudar a ordem.`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        width: 120,
        height: 100,
        flexShrink: 0,
        overflow: 'hidden',
        borderRadius: 2,
        position: 'relative',
        cursor: disabled ? 'default' : 'grab',
        // Só o gesto de segurar arrasta; deslizar continua rolando a página no celular.
        touchAction: 'manipulation',
        userSelect: 'none',
        zIndex: isDragging ? 2 : 'auto',
        opacity: isDragging ? 0.85 : 1,
        border: selected ? '2px solid #1976d2' : '2px solid transparent',
      }}
    >
      <img
        src={image.url || URL.createObjectURL(image)}
        alt={`Foto ${index + 1}`}
        draggable={false}
        style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
      />

      <Chip
        size="small"
        label={isCover ? 'Capa' : index + 1}
        color={isCover ? 'primary' : 'default'}
        sx={{ position: 'absolute', left: 4, bottom: 4, height: 20, ...(isCover ? {} : { bgcolor: 'rgba(0,0,0,0.6)', color: '#fff' }) }}
      />

      {!isCover && (
        <Tooltip title="Usar como capa">
          <IconButton
            size="small"
            aria-label={`Usar a foto ${index + 1} como capa`}
            disabled={disabled}
            sx={{ position: 'absolute', top: 0, left: 0, background: 'rgba(0,0,0,0.5)', color: '#fff' }}
            onClick={(e) => {
              e.stopPropagation();
              onMakeCover();
            }}
          >
            <StarBorderIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {isCover && <StarIcon fontSize="small" sx={{ position: 'absolute', top: 4, left: 4, color: '#ffc107' }} />}

      <IconButton
        size="small"
        aria-label={`Remover a foto ${index + 1}`}
        disabled={disabled}
        sx={{ position: 'absolute', top: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: '#fff' }}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
};

interface Props {
  images: any[];
  currentIndex: number;
  disabled?: boolean;
  onSelect: (index: number) => void;
  onRemove: (index: number, image: any) => void;
  onReorder: (images: any[]) => void;
}

/** Miniaturas das fotos: arrastar muda a ordem e a primeira é a capa. */
const VehicleImageSorter: React.FC<Props> = ({ images, currentIndex, disabled = false, onSelect, onRemove, onReorder }) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const keys = images.map(imageKey);
    onReorder(arrayMove(images, keys.indexOf(String(active.id)), keys.indexOf(String(over.id))));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={images.map(imageKey)} strategy={rectSortingStrategy}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {images.map((image, index) => (
            <SortableImage
              key={imageKey(image)}
              image={image}
              index={index}
              selected={index === currentIndex}
              disabled={disabled}
              onSelect={() => onSelect(index)}
              onRemove={() => onRemove(index, image)}
              onMakeCover={() => onReorder(arrayMove(images, index, 0))}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

export default VehicleImageSorter;
